import { action } from 'mobx';
import type { RootStore } from '~/stores/root.store';
import { AutomationStep } from '~/models/automations-steps/automation-step.model';
import {
    IAutomationInstance,
    TApprovalStatus,
    EMPTY_AUTOMATION_INSTANCE
} from '~/types/automation.types';
import { Automation } from '~/models/automation.model';
import { INVALID_AUTOMATION_ERROR } from '~/utils/automation.utils';
import { trackError } from '~/services/logger/error.utils';
import { sleep } from '~/utils/data.utils';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';

export class AutomationController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async createTemplatesFromCustomEmails() {
        const {
            automationService,
            sessionStore: { currCompanyId },
            automationUiStore: { selectedAutomation },
            toastStore
        } = this.rootStore;

        try {
            const newTemplates =
                await automationService.createTemplatesFromCustomEmails(
                    currCompanyId,
                    selectedAutomation._id
                );
            toastStore.add({
                type: ToastType.Success,
                title: 'Email Templates Created',
                text: newTemplates.join('\n')
            });
        } catch (e) {
            toastStore.add({
                type: ToastType.Error,
                title: 'Failed to create email templates',
                text: e.message
            });
        }
    }

    async validateAutomation() {
        const {
            scheduleCtrl,
            automationUiStore: { selectedAutomation }
        } = this.rootStore;

        if (selectedAutomation.stepsWithScheduleTriggers.length) {
            await scheduleCtrl.fetchCompanyScheduleSessionConfigs();
        }

        if (!selectedAutomation.isValid) {
            throw new Error(INVALID_AUTOMATION_ERROR);
        }
    }

    async fetchAutomationTemplate(automationId: string) {
        const {
            automationService,
            sessionStore: { currCompanyId }
        } = this.rootStore;

        const automation = await automationService.fetchAutomationTemplate(
            currCompanyId,
            automationId
        );
        this.loadAutomation(automation);
    }

    async fetchAutomationPreviews() {
        const {
            automationService,
            automationStore,
            sessionStore: { currCompanyId }
        } = this.rootStore;

        const data = await automationService.fetchAutomationPreviews(
            currCompanyId
        );

        automationStore.setAutomationPreviews(data.workflows);
        return data;
    }

    async updateAutomationTemplate() {
        const {
            automationService,
            automationUiStore: { selectedAutomation }
        } = this.rootStore;

        await this.validateAutomation();

        const automation = await automationService.updateAutomationTemplate(
            selectedAutomation.getDataForServer()
        );
        this.loadAutomation(automation);
        return automation;
    }

    async deleteAutomationTemplate() {
        const {
            automationService,
            automationUiStore: { selectedAutomationId }
        } = this.rootStore;

        await automationService.deleteAutomationTemplate(selectedAutomationId);
    }

    async fetchAutomationInstance() {
        const {
            automationService,
            automationUiStore: { selectedWorkspaceId }
        } = this.rootStore;

        const automation = await automationService.fetchAutomationInstance(
            selectedWorkspaceId
        );
        this.loadAutomation(automation);
        return automation;
    }

    async getTempAutomationInstance(automationId: string) {
        const {
            automationService,
            automationUiStore: { selectedWorkspaceId }
        } = this.rootStore;

        const automation = await automationService.getTempAutomationInstance(
            selectedWorkspaceId,
            automationId
        );
        this.loadAutomation(automation);
        return automation;
    }

    async applyAutomation() {
        const {
            automationService,
            automationUiStore: { selectedAutomation, selectedWorkspaceId }
        } = this.rootStore;

        await this.validateAutomation();

        await automationService.applyAutomation(
            selectedWorkspaceId,
            selectedAutomation.getDataForServer()
        );
        await this.stepPolling();
    }

    async getFlowsWithContract(automationId: string) {
        const {
            automationService,
            sessionStore: { currCompanyId }
        } = this.rootStore;

        const flowsWithContract = await automationService.getFlowsWithContract(
            currCompanyId,
            automationId
        );
        return flowsWithContract;
    }

    async quickApplyAutomation(automationId: string) {
        const {
            automationService,
            automationUiStore: { selectedWorkspaceId }
        } = this.rootStore;

        const automation = await this.getTempAutomationInstance(automationId);
        return automationService.applyAutomation(
            selectedWorkspaceId,
            automation
        );
    }

    async updateAutomationInstance() {
        const {
            automationService,
            automationUiStore: { selectedAutomation, selectedWorkspaceId }
        } = this.rootStore;

        await this.validateAutomation();

        await automationService.updateAutomationInstance(
            selectedWorkspaceId,
            selectedAutomation.getDataForServer()
        );
        await this.stepPolling();
    }

    async deleteAutomationInstance() {
        const {
            automationService,
            automationUiStore: { selectedAutomation, selectedWorkspaceId }
        } = this.rootStore;

        await automationService.deleteAutomationInstance(
            selectedWorkspaceId,
            selectedAutomation._id
        );
        if (selectedAutomation.clone_from_template) {
            await this.getTempAutomationInstance(
                selectedAutomation.clone_from_template
            );
        } else {
            await this.createEmptyAutomationInstance();
        }
    }

    createEmptyAutomationInstance() {
        const {
            automationUiStore: {
                selectedWorkspaceId,
                selectedWorkspace: {
                    event: {
                        event_date,
                        event_timezone,
                        _id: eventId,
                        event_end_date
                    }
                }
            }
        } = this.rootStore;

        const automation = new Automation(
            {
                _id: EMPTY_AUTOMATION_INSTANCE,
                title: 'New Automation',
                is_template: false,
                workflow_steps: [],
                workspace_id: selectedWorkspaceId,
                project_id: eventId,
                project_date: event_date,
                project_timezone: event_timezone,
                project_end_date: event_end_date
            },
            this.rootStore
        );

        this.loadAutomation(automation);
        return automation;
    }

    async executeAutomationStep(step: AutomationStep) {
        const {
            automationService,
            automationUiStore: { selectedWorkspaceId }
        } = this.rootStore;

        const approval_status: TApprovalStatus = step.isApprovalRequired
            ? 'approved'
            : 'not_required';

        await automationService.executeAutomationStep(
            selectedWorkspaceId,
            step._id,
            { approval_status }
        );

        return this.stepPolling(step._id);
    }

    private async stepPolling(stepId?: string) {
        const {
            automationService,
            automationCtrl,
            automationStore,
            automationUiStore: { selectedWorkspaceId }
        } = this.rootStore;

        let tries = 20;
        return (async function polling() {
            const res = await automationService.fetchAutomationInstance(
                selectedWorkspaceId
            );
            const tempAutomation = automationStore.loadTemp(res);
            const executableSteps = tempAutomation.workflow_steps.filter(
                step =>
                    step.isExecutable ||
                    /* There's no flag or computation that can indicate that a step
                    that didn't require approval was executed by the user and should be
                    polled for completion so we need to explicitly pass it to the polling function. */
                    (step._id === stepId && !step.isCompleted)
            );
            if (executableSteps.length && tries) {
                tries--;
                await sleep(500);
                await polling();
            } else {
                automationCtrl.loadAutomation(res);

                if (executableSteps.length) {
                    trackError({
                        error: new Error('Automation: stepPolling error'),
                        context: {
                            executableSteps: executableSteps.map(step =>
                                step.getDataForServer()
                            ),
                            res
                        }
                    });
                }
                if (stepId) {
                    const step = tempAutomation.workflow_steps.find(
                        currStep => currStep._id === stepId
                    );
                    if (!step.isCompleted) {
                        const error = new Error(
                            'Automation: stepPolling error: step execution failed'
                        );
                        trackError({
                            error,
                            context: {
                                step,
                                res
                            }
                        });
                        throw error;
                    }
                }
            }
        })();
    }

    private loadAutomation(automation: IAutomationInstance) {
        const {
            automationStore,
            automationUiStore: {
                selectedWorkspace,
                selectAutomation,
                deselectAutomationStep
            }
        } = this.rootStore;

        deselectAutomationStep();
        automationStore.load(automation);
        automationStore.deleteTemp();
        selectedWorkspace?.updateWorkspaceAutomation(automation);
        selectAutomation(automation._id);
    }

    @action.bound
    async selectAutomationStep(stepId: string) {
        const { automationUiStore, flowCtrl } = this.rootStore;

        automationUiStore.selectAutomationStep(stepId);
        const { selectedStep } = automationUiStore;

        if (selectedStep.isFlow) {
            const flowId = selectedStep.fileTemplateId;
            if (flowId) {
                await flowCtrl.fetchFlowAndLoadToStore(flowId);
                selectedStep.initEmailSettings();
            }
        }
    }

    @action.bound
    async selectFlowTemplate(flowId: string) {
        const {
            automationUiStore: { selectedStep },
            flowCtrl
        } = this.rootStore;

        selectedStep.selectFileTemplate(flowId);

        await flowCtrl.fetchFlowAndLoadToStore(flowId);
        selectedStep.loadFlowConfiguration();
    }

    @action.bound
    async updateEmailTemplate() {
        const {
            companyCtrl,
            automationUiStore: { customEmail, selectedStep }
        } = this.rootStore;

        let emailToUpdate = customEmail;

        if (!customEmail._id) {
            emailToUpdate = await companyCtrl.createEmail(emailToUpdate);
        } else {
            companyCtrl.updateEmail(emailToUpdate);
        }

        selectedStep.selectEmailTemplate(emailToUpdate._id);
    }

    async deleteEmailTemplate() {
        const {
            companyCtrl,
            automationUiStore: { customEmail }
        } = this.rootStore;

        companyCtrl.deleteEmail(customEmail._id);
    }

    reset() {
        this.rootStore.automationStore.reset();
        this.rootStore.automationUiStore.reset();
    }
}
